import Controller from "csi/action/Controller";
import { parseExtendedPath } from "csi/core/extendedPathService";
import def from "csi/def";
import { View } from "csi/def/View";
import csi from "csi/main";
import message from "csi/message";
import CustomViewAttributesStore, { CustomViewAttributesOptions } from "csi/middleware/customView/attributes/CustomViewAttributesStore";
import localeProvider from "csi/page/localeProvider";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { getClasses } from "csi/reflection/reflection";
import { omitSupplierPaths } from "csi/service/reflectionService";
import { getClassDefaultPropertyView } from "csi/service/viewService";
import { readJSONFile } from "csi/utility/htmlUtility";
import csiView from "csi/view";
import { ViewWidget } from "csi/view/Widget";
import declare from "dojo/_base/declare";
import { forEach, groupBy, isEmpty, isEqual, keyBy, sortBy } from "lodash";

// TODO: currently no option from UI to test supplier views, but can set TEST_SUPPLIER_VIEWS to true to do so;
// just note that this will affect the UI after running this action as some extra paths will be omitted
const TEST_SUPPLIER_VIEWS = false;

interface IOptions {
	analyzeDuplicates?: boolean;
	comparisonPaths?: object;
	logVersionPaths?: boolean;
}

interface IPath {
	ignore?: boolean;
	name?: string;
	path?: string;
	oldName?: string;
	oldPath?: string;
}

const VALIDATED_KEYS = {
	ProductHierarchy: 1,

	// IssueContainer (subtask created)
	"HomeIssue|LibMaterial": 1,
	"HomeIssue|MaterialSecurityGroup": 1,
	"HomeIssue|StructureItem": 1,

	// old view - not a concern
	"MigratedCostScenarios|ProductSourcing": 1,
};

const IGNORE_KEYS_ALGORITHM = {
	// matrices previously did not adhere to omitted paths
	"Colorways|FinalInspectionBOM": 1,

	// property view on secondary class - name/path is handled differently due to domain
	"QuoteProperties|FoodSourcing": 1,
	"QuoteProperties|FormulatedMaterialSourcing": 1,
	"QuoteProperties|MaterialSourcing": 1,
	"QuoteProperties|StyleSourcing": 1,
};

const IGNORE_KEYS_VERSION = {
	// helps with limiting report to what has not been analyzed
};

const OMIT_TYPES = {
	CrewHolder: 1,
	DataSheetTable: 1,
	DocAndCommentOwner: 1,
	DSLineItem: 1,
	ImageContainer: 1,
	LocalizedItem: 1,
	VIPExternalIdContainer: 1,
	VIPSortOrderContainer: 1,
};

// These issues will be fixed in new algorithm since it works at domain level (all PreferencePaths should target domain rather than secondary classes)
const SECONDARY_EXCEPTION_PATHS = [
	// due to RealizedProduct.Attributes -> RealizedProductAttributes (RealizedProduct is for Style/Material, but FoodSKUAttributes inherits from RealizedProductAttributes)
	"Child:RealizedProduct(FoodSKU)/Child:Attributes(FoodSKUAttributes)",

	// due to FormulatedProduct.Attributes -> BaseProductAttributes
	"Child:FPItemProduct(Material)/Child:Attributes(MaterialAttributes)",
	"Child:FPItemProduct(Style)/Child:Attributes(StyleAttributes)",
	"(RecipeDataSheet)Child:__Parent__(Material)/Child:Attributes(MaterialAttributes)",
	"(RecipeDataSheet)Child:__Parent__(Style)/Child:Attributes(StyleAttributes)",
];

const VIEW_TYPES = {
	Property: 1,
	Table: 1,
};

function createAttributeStore(view: ViewWidget): CustomViewAttributesStore {
	const { specBuilder } = view;
	const attributesOptions: CustomViewAttributesOptions = {};
	view.onCustomViewAttributeOptions(attributesOptions);

	const attributesStore = new CustomViewAttributesStore(localeProvider, attributesOptions);
	attributesStore.addLevel({ specBuilder });
	return attributesStore;
}

function createView(defView: View): ViewWidget {
	return csiView.newView(defView);
}

/** get paths from attributes store */
function getViewPaths(view: ViewWidget, old?: boolean): IPath[] {
	const attributesStore = createAttributeStore(view);
	let paths: IPath[] = attributesStore.mainLevel.containers.map(({ extendedPath, matrixGroupId, name }) => ({
		name: matrixGroupId ? `${name}:{${matrixGroupId}}` : name,
		path: matrixGroupId ? `${extendedPath.value}:{${matrixGroupId}}` : extendedPath.value,
	}));

	// filter out VIP since module is not included by default in development
	paths = paths.filter((obj) => !obj.name.includes("VIP"));

	if (old) {
		paths = paths.map(({ name, path }) => ({
			oldName: name,
			oldPath: path,
		}));	
	}	

	return paths;
}

function getPathKey(name: string, path: string): string {
	return `${name} - ${path}`;
}

/** log custom view paths by type */
async function logAllClasses(classFunction: (rc: ReflectionClass, json: object, options: IOptions) => void, json: object, options: IOptions): Promise<void> {
	return new Promise((resolve) => {
		const classes = sortBy(getClasses(), (rc) => rc.type);
		const total = classes.length;
		let n = 0;

		csi.forEachWithTimeout(classes, 1, null, (rc) => {
			const percent = Math.floor((n++ / total) * 100);
			message.progress(`Processing ${rc.type} - ${percent}%`);

			try {
				classFunction(rc, json, options);
			} catch (ex) {
				// ignore errors so processing will continue
			}
		}, resolve);
	});
}

/** log custom view duplicate paths for given view */
function logDuplicates(view: ViewWidget, key: string, json: object): void {
	const paths = getViewPaths(view);
	const groups = groupBy(paths, "name");
	const duplicates: Record<string, string[]> = {};

	forEach(groups, (paths, name) => {
		if (paths.length > 1) {
			duplicates[name] = paths.map(({ path }) => path);
		}
	});

	if (!isEmpty(duplicates)) {
		// determine if duplicates resolve to same domain class - if so, then ignore
		const entries = Object.entries(duplicates);

		for (const [name, paths] of entries) {
			const domainPaths = paths.map((path) => parseExtendedPath(path, null, true).resolveToDomain().value);
			const set = new Set(domainPaths);

			if (set.size === 1) {
				delete duplicates[name];
			}
		}
	}

	if (!isEmpty(duplicates)) {
		// log duplicates if for type, or for view and different from those logged with type
		const type = view.defView.getPageClass().type;
		const viewKey = view.defView.getKey();
		const obj = json[type] ||= {};
		const typeDuplicates = obj[type];

		if ((key !== viewKey) || !isEqual(typeDuplicates, duplicates)) {
			obj[key] = duplicates;
		}
	}
}

/** log custom view paths for given view */
function logPaths(view: ViewWidget, key: string, json: object, options: IOptions): void {
	// do nothing if key already exists in json
	if (json[key]) {
		return;
	}	

	const { analyzeDuplicates, comparisonPaths, logVersionPaths } = options;
	
	// omit certain keys based on options
	if (comparisonPaths) {
		if (IGNORE_KEYS_VERSION[key]) {
			return;
		}	
	} else if (IGNORE_KEYS_ALGORITHM[key]) {
		return;
	}

	if (analyzeDuplicates) {
		logDuplicates(view, key, json);
		return;
	}
	
	// if logging paths, this is done in old version so that it can be compared with new version;
	// in other words, these will become comparisonPaths for new version
	if (logVersionPaths) {
		const paths = getViewPaths(view, true);
		json[key] = sortBy(paths, ["oldName"]);
		return;
	}

	// compare paths from current release vs an existing release
	const oldPaths = comparisonPaths[key];
	if (!oldPaths) {
		return;
	}

	const newPaths = getViewPaths(view);
	const newPathsByPath = keyBy(newPaths, ({ path }) => path);
	const oldPathsByCombination = keyBy(oldPaths, ({ oldName, oldPath }) => getPathKey(oldName, oldPath));
	const oldPathsByName = keyBy(oldPaths, "oldName");
	const oldPathsByPath = keyBy(oldPaths, "oldPath");

	const diffPaths = newPaths.filter((obj) => {
		const { name, path } = obj;
		const op = oldPathsByCombination[getPathKey(name, path)];
		if (op) {
			// name and path are same
			op.ignore = true;
			return false;
		}

		const o1 = oldPathsByName[name];
		if (o1) {
			// new path for same name
			o1.ignore = true;
			obj.oldPath = o1.oldPath;
			return true;
		}

		const o2 = oldPathsByPath[path];
		if (o2) {
			// new name for same path
			o2.ignore = true;

			const ep = parseExtendedPath(o2.oldPath, null, true);

			if (ep.endType === "LookupItem") {
				// we know that LookupItem names changed to use subtype, so ignore those
				return false;
			} else if (o2.oldName === `${name} - ${name}`) {
				// in new algorithm, the path name is only applied to the container if different than the type name
				// reference: getCustomViewContainerName
				return false;
			}

			obj.oldName = o2.oldName;
			return true;
		}

		return !VALIDATED_KEYS[key];
	});

	// add old paths that don't match any new name or path
	oldPaths.forEach((obj) => {
		const { ignore, oldName, oldPath } = obj;
		if (!ignore) {
			const ep = parseExtendedPath(oldPath, view.specBuilder.defaultPathHints, false);
			if (ep.isPathToSecondary) {
				const { domainPath, value } = ep;
				if (domainPath) {
					if (newPathsByPath[domainPath.value] || domainPath.isOmitted) {
						// path to secondary class that is now part of domain class
						return;
					}

					// in some cases, the domain class inherits from an abstract class at one level (i.e.BaseRealizedProduct),
					// while the secondary class inherits from an abstract at a different level (i.e. RealizedProductAttributes). When the old
					// algorithm gets all concrete classes of the secondary class, it gets a different set than those of the primary class.
					// Ignore such cases as they should not be part of the new paths.
					if (SECONDARY_EXCEPTION_PATHS.some((path) => value.endsWith(path))) {
						return;
					}
				}
			}

			diffPaths.push({ oldName, oldPath });
		}
	});

	if (diffPaths.length) {
		json[key] = sortBy(diffPaths, ["name", "oldName"]);
	}
}

/** log custom view paths by type */
function logType(rc: ReflectionClass, json: object, options: IOptions): void {
	const { alias, isSecondary, type } = rc;

	if (!alias || isSecondary || OMIT_TYPES[type]) {
		return;
	}

	const defView = getClassDefaultPropertyView(rc, {
		bReadOnly: true,
		supportsAttributeGroups: false,
	});

	const view = createView(defView);
	logPaths(view, type, json, options);
}

/** log custom view paths for all property views and others that have custom configuration */
function logViews(rc: ReflectionClass, json: object, options: IOptions): void {
	const { type } = rc;
	const defType = def.typeDefinition(type);

	defType?.traverseViewTags((viewTag) => {
		const defView = defType._createViewDef(viewTag);
		const { m_bSlim, pathHints, prefPaths, m_typeView } = defView;
		const rel = defView.getPageRelationship();
		const actualPathHints = rel?.pathHints || pathHints;

		// only analyze non-slim views with attributes
		if (!VIEW_TYPES[m_typeView] || m_bSlim) {
			return;
		}

		// skip view if it has nothing special in terms of paths, although process all property views (no relationship)
		if (!actualPathHints && !prefPaths) {
			return;
		}

		defView.m_rcRoot = rc;
		const view = createView(defView);
		logPaths(view, defView.getKey(), json, options);
	});
}

export = declare(Controller, {
	/** @property {boolean} analyzeDuplicates - analyze duplicate paths (same name, different path) in system */
	analyzeDuplicates: false,

	/** @property {boolean} logVersionPaths - log paths for all types and views - no diff */
	logVersionPaths: false,

	/** @property {boolean} useCurrentView - execute for current view rather than all types/views */
	useCurrentView: false,

	async process(): Promise<void> {
		const { analyzeDuplicates, m_action, m_formData, logVersionPaths, useCurrentView, widget } = this;
		const json = {};
		const field = m_formData?.getField("CustomViewPathFile");
		let comparisonPaths: object;

		if (field) {
			const fileInput = field.m_control as HTMLInputElement;
			const file = fileInput.files[0];
			comparisonPaths = await readJSONFile(file);
		}

		if (TEST_SUPPLIER_VIEWS) {
			omitSupplierPaths();
		}

		const options = { analyzeDuplicates, comparisonPaths, logVersionPaths };

		if (useCurrentView) {
			// analyze current view
			logPaths(widget, widget.defView.getKey(), json, options);
		} else {
			// no need to check type paths when testing supplier views
			if (!TEST_SUPPLIER_VIEWS) {
				await logAllClasses(logType, json, options);
			}

			await logAllClasses(logViews, json, options);
		}

		const title = m_action.m_name || m_action.m_id;
		const { document } = window.open("", title);
		document.write(`<HTML><HEAD><TITLE>${title}</TITLE></HEAD>`);
		document.write("<BODY><pre>");
		document.write(csi.objectToString(json, 0, "\n"));
		document.write("</pre></BODY></HTML>");
		document.close();

		this.postProcess();
	},
});
